import { useEffect, useState } from 'react';
import { getMeineRechte } from '@/services/api';
import type { ZugangBereich, ZugangFachbereich } from '@/types';

/**
 * Die eigenen Rechte — im Modul-Cache, aber nicht mehr fuer die Ewigkeit.
 *
 * Frueher wurde einmal je Sitzung geladen. Wer die App offen hatte, waehrend
 * die Geschaeftsfuehrung etwas freigeschaltet hat, sah es nie — bei einem
 * Monteur, der das Handy den ganzen Tag nicht neu startet, praktisch nie.
 * Deshalb wird jetzt nachgeladen, wenn die App wieder in den Vordergrund
 * kommt und der Stand aelter ist als die Frist.
 *
 * Solange nichts geladen ist, ist `geladen` false und die Oberflaeche verhaelt
 * sich wie bisher. Das ist Absicht: beim ersten Rendern nichts wegzunehmen
 * ist harmloser, als kurz alles auszublenden und dann zurueckzuholen.
 *
 * Fail-open bleibt Fail-open: `darf()` gibt true, solange nichts geladen ist,
 * und ein fehlgeschlagenes Nachladen sperrt niemanden aus — dann bleibt
 * einfach der alte Stand stehen. Die Pruefung, die zaehlt, sitzt im Backend.
 *
 * Wer nicht in der Berechtigungsliste steht, bekommt vom Server die aus seiner
 * Rolle abgeleiteten Rechte (`quelle: 'rolle'`). Fuer den aendert sich also nichts,
 * bis die Geschaeftsfuehrung ihn ausdruecklich eintraegt.
 */
type Stand = { rechte: ZugangBereich[]; quelle: string; fachbereich: ZugangFachbereich | null };

/**
 * Wie lange ein geladener Stand als frisch gilt. Zwei Minuten sind der
 * Kompromiss: kurz genug, dass „ich hab dir das freigeschaltet, schau mal"
 * beim naechsten Griff zum Handy schon stimmt — lang genug, dass hektisches
 * Hin- und Herwechseln zwischen Apps keine Anfrageflut ausloest. Einen Timer
 * gibt es bewusst nicht; geholt wird nur, wenn jemand wirklich hinsieht.
 */
export const ZUGANG_FRIST_MS = 2 * 60 * 1000;

/**
 * Ladezustand — bewusst getrennt von `quelle`. `quelle` ist eine
 * Herkunftsangabe des Servers („liste", „rolle") und wurde hier als
 * Ladezustand missbraucht; damit liessen sich „laedt noch" und „Laden
 * fehlgeschlagen" nicht unterscheiden. Genau diese Unterscheidung braucht die
 * Navigation: waehrend des Ladens nichts zeichnen, bei einem Fehlschlag alles.
 */
type Ladezustand = 'unberuehrt' | 'laeuft' | 'da' | 'fehlgeschlagen';

let cache: Stand | null = null;
let zustand: Ladezustand = 'unberuehrt';
let geladenAm = 0;
let laeuft: Promise<void> | null = null;
/**
 * Zaehler statt Flag: er entwertet Ladungen, die beim Abmelden schon
 * unterwegs waren. Ohne ihn koennte eine spaet eintreffende Antwort die
 * Rechte des vorigen Nutzers in den frisch geleerten Cache schreiben.
 */
let generation = 0;

/** Alle Nutzer des Hooks — ein Nachladen muss jeden erreichen, nicht nur den Aussloeser. */
const abonnenten = new Set<() => void>();

/**
 * Ein gemeinsamer Schnappschuss statt Einzelwerten: neue Identitaet nur, wenn
 * sich wirklich etwas geaendert hat. So zeichnet ein Nachladen ohne Aenderung
 * nichts neu und es flackert nichts.
 */
type Schnappschuss = { stand: Stand | null; zustand: Ladezustand };
let schnappschuss: Schnappschuss = { stand: null, zustand: 'unberuehrt' };

function melden(): void {
  schnappschuss = { stand: cache, zustand };
  abonnenten.forEach((fn) => fn());
}

/** Nach dem Abmelden wegwerfen — sonst erbt der naechste Nutzer die Rechte. */
export function zugangVergessen(): void {
  cache = null;
  zustand = 'unberuehrt';
  geladenAm = 0;
  laeuft = null;
  generation += 1;
  melden();
}

/**
 * Holt die Rechte neu, wenn der Stand aelter ist als die Frist.
 * `erzwingen` uebergeht die Frist; laeuft schon eine Ladung, wird die geteilt.
 */
export function zugangAktualisieren(erzwingen = false): Promise<void> {
  if (laeuft) return laeuft;
  if (!erzwingen && cache && Date.now() - geladenAm < ZUGANG_FRIST_MS) return Promise.resolve();

  const meine = generation;
  // Nur der Kaltstart ist ein Ladezustand. Liegt schon ein Stand vor, bleibt
  // er sichtbar — ein Nachladen darf die Navigation nicht leerraeumen.
  if (!cache && zustand !== 'laeuft') {
    zustand = 'laeuft';
    melden();
  }
  laeuft = getMeineRechte()
    .then((r) => {
      // Zwischendurch abgemeldet: Antwort gehoert zum alten Nutzer, wegwerfen.
      if (meine !== generation) return;
      cache = {
        rechte: r.rechte ?? [],
        quelle: r.quelle,
        fachbereich: r.fachbereich ?? null,
      };
      zustand = 'da';
      geladenAm = Date.now();
      melden();
    })
    .catch(() => {
      // Kein Netz oder Endpunkt aus: nicht aussperren, sondern durchlassen.
      // Der alte Stand bleibt stehen — keine Kachel verschwindet deswegen.
      // Die Pruefung, die zaehlt, sitzt ohnehin im Backend.
      if (meine !== generation || cache) return;
      // Kaltstart fehlgeschlagen: raus aus dem Ladezustand, sonst wartet die
      // Navigation ewig auf etwas, das nicht mehr kommt.
      zustand = 'fehlgeschlagen';
      melden();
    })
    .finally(() => {
      // Nur die eigene Ladung abmelden — nach zugangVergessen() laeuft
      // moeglicherweise schon die naechste.
      if (meine === generation) laeuft = null;
    });

  return laeuft;
}

export function useZugang(): {
  rechte: ZugangBereich[];
  quelle: string | null;
  geladen: boolean;
  /**
   * Kaltstart laeuft noch — es ist NICHTS bekannt. Wer Navigation zeichnet,
   * zeichnet hier gar nichts (oder einen ruhigen Platzhalter): sonst blitzen
   * beim Neuladen alle Kacheln auf und verschwinden wieder, was nach Fehler
   * aussieht und Module zeigt, die jemand nicht sehen soll.
   */
  laedt: boolean;
  /**
   * Kaltstart ist fehlgeschlagen (kein Netz, Endpunkt aus). Dann gilt weiter
   * Fail-open: alles zeigen. Ein Netzausfall darf niemanden aus seiner
   * eigenen App aussperren — die Pruefung, die zaehlt, sitzt im Backend.
   */
  fehlgeschlagen: boolean;
  darf: (bereich: ZugangBereich) => boolean;
  /**
   * Primaerer Fachbereich. Anders als `darf` ist das KEIN Fail-open: solange
   * nichts geladen ist, ist er null. Wer daran eine Kachel haengt, prueft
   * zusaetzlich `geladen` — sonst blitzt sie beim ersten Rendern auf.
   */
  fachbereich: ZugangFachbereich | null;
} {
  const [eigen, setEigen] = useState(schnappschuss);
  const stand = eigen.stand;

  useEffect(() => {
    // Der Cache ist Modul-Zustand. Ueber die Abonnentenliste bekommt jede
    // Komponente den neuen Stand, auch wenn eine andere ihn geholt hat.
    const uebernehmen = () => setEigen(schnappschuss);
    abonnenten.add(uebernehmen);
    uebernehmen();
    void zugangAktualisieren();

    const beiRueckkehr = () => {
      if (document.visibilityState !== 'visible') return;
      void zugangAktualisieren();
    };
    // visibilitychange deckt Handy-Sperre und App-Wechsel ab, focus den
    // Fenster-Wechsel am Desktop. Die Frist verhindert, dass daraus bei
    // jedem Wechsel eine Anfrage wird.
    document.addEventListener('visibilitychange', beiRueckkehr);
    window.addEventListener('focus', beiRueckkehr);

    return () => {
      abonnenten.delete(uebernehmen);
      document.removeEventListener('visibilitychange', beiRueckkehr);
      window.removeEventListener('focus', beiRueckkehr);
    };
  }, []);

  const geladen = !!stand;
  // „unberuehrt" zaehlt als Laden: der Effekt startet die Ladung im selben
  // Durchgang, und ein Bild fuer einen Bruchteil einer Sekunde ist genau das,
  // was hier weg soll.
  const laedt = !stand && eigen.zustand !== 'fehlgeschlagen';
  return {
    rechte: stand?.rechte ?? [],
    quelle: stand?.quelle ?? null,
    geladen,
    laedt,
    fehlgeschlagen: !stand && eigen.zustand === 'fehlgeschlagen',
    // Solange nichts geladen ist, wird nichts verboten.
    darf: (bereich) => (geladen ? (stand?.rechte ?? []).includes(bereich) : true),
    fachbereich: stand?.fachbereich ?? null,
  };
}
